import { type Answer, OTHER, answerIds, followUpLevel, isEmpty } from "./answers";
import type { Question, QuestionnaireRevisionContents } from "./contents";
import { visibleQuestions } from "./visibility";

export type Problem = {
    question: string;
    kind: "required" | "tooLong" | "format" | "outOfRange" | "other" | "comment" | "file" | "expiry";
};

const formats: Record<string, RegExp> = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    url: /^(https?:\/\/)?[^\s.]+(\.[^\s.]+)+\S*$/i,
    phone: /^\+?[\d\s().-]{5,}$/,
};

const kindsFor = (question: Question, answer: Answer | undefined): Problem["kind"][] => {
    if (question.type === "signature")
        return question.required && isEmpty(answer?.value) && !answer?.files?.length ? ["required"] : [];
    const value = answer?.value;
    if (isEmpty(value)) return question.required ? ["required"] : [];

    const kinds: Problem["kind"][] = [];
    if (question.type === "text" && typeof value === "string") {
        if (question.maxChars && value.length > question.maxChars) kinds.push("tooLong");
        const pattern = formats[question.format ?? "plain"];
        if (pattern && !pattern.test(value.trim())) kinds.push("format");
    }
    if (question.type === "number") {
        const n = Number(value);
        if ((question.min !== undefined && n < question.min) || (question.max !== undefined && n > question.max))
            kinds.push("outOfRange");
    }
    if (answerIds(value).includes(OTHER) && !answer?.other?.trim()) kinds.push("other");
    if (question.type === "file" && question.expiry && !answer?.expiry) kinds.push("expiry");
    if (followUpLevel(question, answer, "comment") === "required" && !answer?.comment?.trim()) kinds.push("comment");
    if (followUpLevel(question, answer, "file") === "required" && !answer?.files?.length) kinds.push("file");
    return kinds;
};

export const problems = (contents: QuestionnaireRevisionContents, answers: Record<string, Answer>): Problem[] =>
    visibleQuestions(contents, answers).flatMap((question) =>
        kindsFor(question, answers[question.id]).map((kind) => ({ question: question.id, kind }))
    );
